/**
 * Formatação de **tamanho** e de **saldo** — o que a galeria de armazenamento e
 * o bloco de limites imprimem.
 *
 * Veio de `apps/web/src/lib/plans/format-limit.ts` (`formatBytes`) e de
 * `apps/web/src/lib/plans/format-usage-amount.ts` quando o app ganhou a aba
 * "Armazenamento" de Gerenciar conta. O web reexporta dos caminhos antigos.
 *
 * O `formatLimit` **não** subiu junto: ele depende de `ResolvedFeature` e da
 * copy da vitrine (`showcase-features.ts`), e o app ainda não mostra vitrine.
 */

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = BYTES_IN_KB * 1024;
const BYTES_IN_GB = BYTES_IN_MB * 1024;

/**
 * Exportado porque o bloco de limites precisa formatar também o **consumo**,
 * não só o teto. Sem isso o armazenamento aparece como "5242880 de 800 MB":
 * o teto passava pelo formatador, o consumo ia cru.
 */
export function formatBytes(stored: number): string {
  if (stored >= BYTES_IN_GB && stored % BYTES_IN_GB === 0) {
    return `${stored / BYTES_IN_GB} GB`;
  }
  if (stored >= BYTES_IN_MB) {
    return `${Math.round((stored / BYTES_IN_MB) * 10) / 10} MB`;
  }
  // Abaixo de 1 MB, o corte para KB evita que um teto real (ex.: 500000
  // bytes) vire "0 MB" na tela — indistinguível do teto zerado de verdade.
  // `stored` não-zero nunca pode arredondar para "0 KB".
  if (stored === 0) return "0 KB";
  return `${Math.max(1, Math.round(stored / BYTES_IN_KB))} KB`;
}

/**
 * Formata **consumo**, não teto. Quantidade de coisas se lê como número;
 * armazenamento, não. A unidade da linha decide.
 */
export function formatUsageAmount(value: number, unit: string | null): string {
  return unit === "bytes" ? formatBytes(value) : String(value);
}

// `describeRemaining` mora em `storage-panel.ts`: ela é consumida só por
// `measureUsage`, lá, e assim referencia `STORAGE_PANEL_COPY` (o texto do
// saldo) sem criar um ciclo com `storage-panel.copy.ts` — que já importa
// `formatBytes` daqui.
